import { useEffect, useReducer } from 'react';

// Themes with a /patterns/{theme} page, from the webmaster-editable vocabulary (GET
// /api/Patterns/themes), for the filter dropdown, the filter chip and the move panel's link. The seed
// below (PatternTaxonomy.SeedDefaults) is the fallback until the fetch lands or if it fails.

type ThemeItem = { Theme: string; Title: string; Order: number };

// Fallback = the seeded defaults, in seed order. Keep in sync with PatternTaxonomy.SeedDefaults.
const SEED_ITEMS: ThemeItem[] = [
  { Theme: 'hangs-piece', Title: 'Hanging a piece', Order: 10 },
  { Theme: 'hangs-major-piece', Title: 'Hanging a rook or queen', Order: 20 },
  { Theme: 'missed-win', Title: 'Missing a winning move', Order: 30 },
  { Theme: 'loses-exchange', Title: 'Losing the exchange', Order: 40 },
  { Theme: 'drops-pawn', Title: 'Dropping pawns', Order: 50 },
  { Theme: 'squanders-advantage', Title: 'Throwing away a winning position', Order: 60 },
  { Theme: 'loses-advantage', Title: 'Handing over the advantage', Order: 70 },
  { Theme: 'allows-mate', Title: 'Walking into mate', Order: 80 },
];

function toMap(items: ThemeItem[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const i of items) {
    map[i.Theme] = i.Title;
  }
  return map;
}

// Module-level cache shared by every consumer, with a tiny subscription so hooks re-render on load.
let items: ThemeItem[] = SEED_ITEMS;
let labelMap: Record<string, string> = toMap(SEED_ITEMS);
let started = false;
const listeners = new Set<() => void>();

export function loadPatternThemes(): void {
  if (started) {
    return;
  }
  started = true;
  fetch('/api/Patterns/themes')
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then((data: ThemeItem[]) => {
      // Empty is legitimate (every page disabled); only a failed fetch keeps the seed.
      items = [...data].sort((a, b) => a.Order - b.Order);
      labelMap = toMap(items);
      listeners.forEach((l) => l());
    })
    .catch(() => {
      /* keep the seed */
    });
}

// The label for a theme with an enabled page, else null (no link).
export function patternLabel(theme: string | null | undefined): string | null {
  if (!theme) {
    return null;
  }
  return labelMap[theme] ?? null;
}

// [{ theme, label }] for the filter dropdown; loads and re-renders when ready.
export function usePatternThemes(): { theme: string; label: string }[] {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    listeners.add(force);
    loadPatternThemes();
    return () => {
      listeners.delete(force);
    };
  }, []);
  return items.map((i) => ({ theme: i.Theme, label: i.Title }));
}
